import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { ClinicianRole } from '../programs/schema';
import { escalationClaimSchema } from './claims';

// ─── open-escalation queue ──────────────────────────────────────────────────
//
// Wire vocabulary for the staff read that lists every open safety escalation
// across members — the data behind the Atlas escalation review queue.
//
// The item is deliberately the smallest thing the queue can render and link
// from: which escalation, which member (by Galapagos id only), when it was
// raised, and who on staff holds the member's primary care-team assignment.
// No member name, no `reason`, no transcript, no source. Opening an item goes
// to the member detail page, which sits behind the member access gate, so the
// list itself never needs to reveal who a member is.
//
// The primary assignee's name is staff personal data shown on purpose:
// escalations include suicide-risk situations, and the person picking one up
// needs to see at a glance who already owns the member to coordinate with them.

/** Oldest first is the default: with no severity tiers, waiting time is the urgency signal. */
export const ESCALATION_QUEUE_ORDER = ['asc', 'desc'] as const;
export const escalationQueueOrderSchema = z.enum(ESCALATION_QUEUE_ORDER);
export type EscalationQueueOrder = z.infer<typeof escalationQueueOrderSchema>;

/** A full uuid's length: the longest prefix that can still match one. */
export const ESCALATION_QUEUE_MEMBER_ID_MAX_LENGTH = 36;

/**
 * Query params (wire shape — optional strings, clamped and decoded server-side).
 *
 * `.strict()`: a REQUEST schema, so an unknown parameter is refused rather than
 * silently ignored (ADR-135 Rule 1). A caller sending a filter this route does
 * not have would otherwise read a full queue as a filtered one.
 *
 * `memberId` is a case-insensitive PREFIX of the member's uuid. Its character
 * set is fixed here — hex digits and hyphens only — so `%` and `_` can never
 * reach the `LIKE` the repo builds from it. A blank term passes and means "no
 * filter".
 */
export const escalationQueueQuerySchema = z
  .object({
    order: escalationQueueOrderSchema.optional(),
    memberId: z
      .string()
      .trim()
      .max(ESCALATION_QUEUE_MEMBER_ID_MAX_LENGTH)
      .regex(/^[0-9a-fA-F-]*$/)
      .optional(),
    limit: z.string().optional(),
    cursor: z.string().optional(),
  })
  .strict();
export type EscalationQueueQuery = z.infer<typeof escalationQueueQuerySchema>;

/**
 * The member's open primary care-team assignee, as staff see it in the queue.
 *
 * `clinicalRole` is the assignment's role, never the person's org role.
 * `name` is null when the staff record yields no display name at all; the
 * client owns the copy for that case, so the API never authors display text.
 * `provisional` marks a system pick nobody has confirmed yet, and
 * `deactivated` a primary whose staff account is disabled while the
 * assignment is still open: both are decided once, on the server, so the
 * client renders flags rather than re-deriving them from provenance columns.
 * Non-strict for the same reason as the item below (ADR-135 Rule 1).
 */
export const escalationQueuePrimaryAssigneeSchema = z.object({
  name: z.string().min(1).nullable(),
  clinicalRole: ClinicianRole,
  provisional: z.boolean(),
  deactivated: z.boolean(),
});
export type EscalationQueuePrimaryAssignee = z.infer<typeof escalationQueuePrimaryAssigneeSchema>;

/**
 * One queue row. `raisedAt` is a UTC instant with the stored microsecond
 * precision, exactly as the database renders it.
 *
 * Not `.strict()`, here or on the objects below: Atlas parses responses with
 * `schema.parse()`, so a strict response would fail every bundle older than a
 * deploy that adds a field (ADR-135 Rule 1).
 */
export const escalationQueueItemSchema = z.object({
  escalationId: z.string().uuid(),
  // The shared member-id grammar, not `.uuid()`: members imported from backend2
  // keep an id with no RFC version or variant guarantee, and a strict validator
  // here would fail the whole page's response validation on one of them.
  memberId: memberIdSchema,
  raisedAt: z.iso.datetime(),
  /** Null when the member has no open primary assignment. */
  primaryAssignee: escalationQueuePrimaryAssigneeSchema.nullable(),
  // Who holds this escalation ("In progress"), or null when it is free. Added
  // in place under ADR-135 Rule 2(a): this response schema has been non-strict
  // since the endpoint first shipped, so no bundle that can call it rejects an
  // unknown key. Optional so a new bundle reading an API that predates the
  // field renders "Open" (Rule 6); a reader treats absent like null.
  claim: escalationClaimSchema.nullable().optional(),
});
export type EscalationQueueItem = z.infer<typeof escalationQueueItemSchema>;

/**
 * A keyset page plus the open count. `nextCursor` is null on the last page.
 * `openCount` counts every open escalation across members, whatever the
 * request filtered or paged by — it feeds the navigation badge, which must not
 * change with the search box.
 */
export const escalationQueueDataSchema = z.object({
  items: z.array(escalationQueueItemSchema),
  nextCursor: z.string().nullable(),
  openCount: z.number().int().nonnegative(),
});
export type EscalationQueueData = z.infer<typeof escalationQueueDataSchema>;

export const escalationQueueResponseSchema = apiEnvelope(escalationQueueDataSchema);
export type EscalationQueueResponse = z.infer<typeof escalationQueueResponseSchema>;
